import {
  SEGMENT_LABEL,
  MONTH_KEYS,
  fmtCount,
  fmtNinku,
  fmtPercent,
  fmtQuantity,
  fmtText,
  monthLabel,
  type Case,
  type World,
} from '@kdk/shared';
import { tokens } from '@kdk/shared/theme';
import { Box, Button, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useWorld } from '../../api/queries';
import { AllocationBar } from '../../shared-ui/AllocationBar';
import { AsyncBoundary } from '../../shared-ui/AsyncBoundary';
import { DrawerSkeleton } from '../../shared-ui/skeletons';
import { DestChip } from '../../shared-ui/DestChip';
import { DetailDrawer, type BackLink } from '../../shared-ui/DetailDrawer';
import { TableScroll } from '../../shared-ui/TableScroll';
import { KeyValueList } from '../../shared-ui/KeyValueList';
import { buildCaseDetail } from './model/caseDetail';
import { useDrawerParams } from './useDrawerParams';

const CaseBody = ({ caseItem, world }: { caseItem: Case; world: World }) => {
  const navigate = useNavigate();
  const { open } = useDrawerParams();
  const detail = useMemo(
    () => buildCaseDetail(caseItem, world.staff, new Set(world.cases.map((c) => c.id))),
    [caseItem, world.staff, world.cases],
  );
  const period = `${MONTH_KEYS[caseItem.startMonth] ?? '—'} 〜 ${MONTH_KEYS[caseItem.endMonth] ?? '—'}`;

  return (
    <>
      <Typography variant="h2" sx={{ fontSize: 18, mt: '4px', mb: '12px' }}>
        {caseItem.name}
      </Typography>
      <KeyValueList
        items={[
          {
            label: '案件ID',
            value: (
              <>
                {caseItem.id}
                {caseItem.dedicatedAgent && (
                  <Box component="span" sx={{ ml: 1 }}>
                    <DestChip dest="G" label="現場代理人 専任" />
                  </Box>
                )}
              </>
            ),
          },
          { label: '顧客', value: fmtText(caseItem.customer) },
          {
            label: '工種／区分',
            value: `${fmtText(caseItem.kind)}／${caseItem.segment === null ? '—' : SEGMENT_LABEL[caseItem.segment]}`,
          },
          { label: '期間', value: period },
          {
            label: '必要量',
            value: `${fmtQuantity(caseItem.requiredNinku)}人工（${monthLabel(detail.month)}時点 配置 ${fmtNinku(detail.fte)}人工・${fmtCount(detail.headCount, '名')}）`,
          },
        ]}
      />
      <Box sx={{ borderTop: `1px solid ${tokens.line}`, pt: '12px', mt: '14px' }}>
        <Typography variant="h3" sx={{ color: 'text.secondary', mb: '8px' }}>
          関与メンバー（{monthLabel(detail.month)}）　この案件への配分／本人の総負荷
        </Typography>
        <TableScroll ariaLabel="関与メンバーの表" axis="x" variant="plain">
          <Table size="small">
            <TableBody>
              {detail.members.map((m) => (
                <TableRow
                  key={m.staff.id}
                  hover
                  tabIndex={0}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    open('staff', m.staff.id, { from: { kind: 'case', id: caseItem.id } });
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter')
                      open('staff', m.staff.id, { from: { kind: 'case', id: caseItem.id } });
                  }}
                >
                  <TableCell>
                    {m.staff.name}
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                      {fmtText(m.role)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{fmtPercent(m.percent)}</TableCell>
                  <TableCell>
                    <AllocationBar staff={m.staff} month={detail.month} />
                  </TableCell>
                  <TableCell align="right" sx={{ color: m.load > 100 ? tokens.red : undefined }}>
                    {fmtPercent(m.load)}
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                      {fmtCount(m.caseCount, '件')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableScroll>
        {detail.members.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            この月に配置されているメンバーはいません。
          </Typography>
        )}
      </Box>
      {detail.shortage > 0 && (
        <Box sx={{ borderTop: `1px solid ${tokens.line}`, pt: '12px', mt: '14px' }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              void navigate(`/search?target=${encodeURIComponent(caseItem.id)}`);
            }}
          >
            配置候補を探す
          </Button>
        </Box>
      )}
    </>
  );
};

export const CaseDrawer = ({
  id,
  onClose,
  back,
}: {
  id: string;
  onClose: () => void;
  back?: BackLink | undefined;
}) => {
  const world = useWorld();
  const title =
    world.status === 'success'
      ? (world.data.cases.find((c) => c.id === id)?.name ?? '案件')
      : '案件';
  return (
    <DetailDrawer title={title} onClose={onClose} back={back}>
      <AsyncBoundary state={world} skeleton={<DrawerSkeleton />}>
        {(w) => {
          const caseItem = w.cases.find((c) => c.id === id);
          return caseItem === undefined ? (
            <Typography color="text.secondary" sx={{ py: 4 }}>
              該当する案件が見つかりません。
            </Typography>
          ) : (
            <CaseBody caseItem={caseItem} world={w} />
          );
        }}
      </AsyncBoundary>
    </DetailDrawer>
  );
};
